import { CompanyAnnualRevenue } from '@aibees/elephant/components/company-annual-revenue';
import { LabelActionCell } from '@aibees/elephant/components/label-action';
import { PeopleProfile } from '@aibees/elephant/components/people-profile';
import { PersonTitle } from '@aibees/elephant/components/person-title';
import { SearchDataNotFound } from '@aibees/elephant/components/search-data-not-found';
import { TableLoadingSpinner } from '@aibees/elephant/components/table-loading-spinner';
import { useBrowseSearchIds } from '@aibees/elephant/hooks/use-browse-search-ids';
import { ElephantPresetType } from '@aibees/elephant/types';
import { LabelType } from '@aibees/elephant/types/label';
import {
  formatCompanyLocation,
  humanReadableSeniority,
} from '@aibees/elephant/utils';
import {
  TableCellTypography,
  TableColumnMetadata,
} from '@aibees/shared/components';
import { coloredIcons } from '@aibees/shared/constants/icons';
import { AdvanceTableConfig } from '@aibees/shared/features';
import { capitalize } from '@aibees/shared/utils';
import { PeopleUnlock } from '../../components/people-unlock';
import { DataViewSwitcher } from '../search-table-company/components/data-view-switcher';
import { LookalikeButtonWithSharedData } from '../search-table-company/components/lookalike-button/lookalike-button-with-shared-data';
import { SearchTableHeader } from '../search-table-company/components/search-table-header';
import { CompanyOfPerson } from './components/company-of-person';
import { PeopleQuickInfo } from './components/people-quick-info';
import { useBrowsePeopleSearchForTable } from './hooks/use-browse-people-search-for-table';
import { PeopleTableInfo } from './types';

export const PEOPLE_SEARCH_API_RESPONSE_PERF_MARK =
  'people-search-api-response';

const peopleMetadata: TableColumnMetadata = {
  ...coloredIcons.people,
  type: 'People',
};

const companyMetadata: TableColumnMetadata = {
  ...coloredIcons.company,
  type: 'Company',
};

export const config: AdvanceTableConfig<PeopleTableInfo> = {
  rowKey: 'id',
  rowSelectable: true,
  showSelectAll: true,
  paidBulkSelection: true,
  paidPagination: true,
  columnModifierKey: 'people-search-table',
  columnModifierPresetType: ElephantPresetType.PEOPLE_SEARCH_TABLE_LAYOUT,
  exportSettingPresetType:
    ElephantPresetType.PEOPLE_SEARCH_EXPORT_SETTINGS_COLUMNS,
  loadingText: 'Loading...',
  loadingComponent: <TableLoadingSpinner />,
  columns: [
    {
      key: 'fullName',
      label: 'Name',
      size: 'large',
      disabledInColumnModifier: true,
      render: (_, v) => (
        <PeopleProfile
          socialLink={v.link}
          name={v.profile?.full_name}
          id={v?.id}
          imageSrc={v?.profile?.picture?.source}
          data={v}
          showPopover
          avatarSize={40}
        />
      ),
      isSticky: true,
      metadata: peopleMetadata,
    },
    {
      key: 'title',
      label: 'Title',
      size: 'medium',
      disabledInColumnModifier: true,
      metadata: peopleMetadata,
      render: (_, v) => <PersonTitle data={v} />,
    },
    {
      key: 'lookalike',
      label: 'Lookalike',
      size: 'small',
      render: (_, v) => (
        <LookalikeButtonWithSharedData currentItem={v} fullWidth />
      ),
      disabledInColumnModifier: true,
      metadata: peopleMetadata,
    },
    {
      key: 'quick-info',
      label: 'Quick Info',
      width: 'fit-content',
      size: 'medium',
      align: 'left',
      disabledInColumnModifier: true,
      metadata: peopleMetadata,
      render: (_, v) => <PeopleQuickInfo item={v} />,
    },
    {
      key: 'quick-action',
      label: 'Quick Action',
      size: 'small',
      align: 'left',
      disabledInColumnModifier: true,
      metadata: peopleMetadata,
      render: (_, v) => <PeopleUnlock item={v} fullWidth />,
    },
    {
      id: 'company',
      key: 'company.name',
      label: 'Current Company ',
      size: 'medium',
      render: (_, v) => <CompanyOfPerson data={v} />,
      disabledInColumnModifier: true,
      metadata: companyMetadata,
    },
    {
      id: 'location',
      label: 'Contact Location',
      size: 'medium',
      align: 'left',
      metadata: peopleMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={
            capitalize(
              [v.location?.country, v.location?.state, v.location?.city]
                .filter(Boolean)
                .join(', ')
            ) || ''
          }
        />
      ),
    },
    {
      key: 'company.size',
      label: 'Headcount',
      size: 'medium',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={capitalize(
            v.company?.summary?.staff?.total?.toLocaleString() ?? ''
          )}
        />
      ),
    },
    {
      key: 'department',
      label: 'Department',
      size: 'medium',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={capitalize(
            v.department?.departments
              ?.map((i) => i.replace('_', ' '))
              .join(', ')
          )}
        />
      ),
    },
    {
      key: 'seniority',
      label: 'Seniority',
      size: 'medium',
      metadata: peopleMetadata,
      align: 'left',
      render: (_, v) => (
        <TableCellTypography
          item={humanReadableSeniority(v.department?.seniority)}
        />
      ),
    },
    {
      key: 'company-location',
      label: 'Company Location',
      size: 'medium',
      align: 'left',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={
            capitalize(
              formatCompanyLocation([
                v.company?.location?.headquarter?.country,
                v.company?.location?.headquarter?.state,
                v.company?.location?.headquarter?.city,
              ])
            ) || ''
          }
        />
      ),
    },

    {
      key: 'industry',
      label: 'Primary Industry',
      align: 'left',
      size: 'medium',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography item={capitalize(v.company?.summary?.industry)} />
      ),
    },
    {
      key: 'industryTags',
      label: 'Industry Tags',
      align: 'left',
      size: 'medium',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={capitalize(v.company?.industries?.join(', '))}
        />
      ),
    },
    {
      key: 'product-and-services',
      label: 'Product & Services',
      size: 'medium',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography
          item={capitalize(
            v.company?.keywords?.filter(Boolean)?.join(', ') ?? ''
          )}
        />
      ),
    },
    {
      key: 'company-type',
      label: 'Company Type',
      size: 'medium',
      metadata: companyMetadata,
      render: (_, v) => (
        <TableCellTypography item={capitalize(v.company?.summary?.type)} />
      ),
    },
    {
      key: 'contact-lists',
      label: 'Contact Lists',
      align: 'left',
      size: 'large',
      metadata: peopleMetadata,
      render: (_, v) => (
        <LabelActionCell
          id={v.id}
          type={LabelType.LIST}
          currentLabelIds={v.labels}
        />
      ),
    },
    {
      key: 'contact-labels',
      label: 'Contact Labels',
      size: 'large',
      metadata: peopleMetadata,
      render: (_, v) => (
        <LabelActionCell
          id={v.id}
          type={LabelType.LABEL}
          currentLabelIds={v.labels}
        />
      ),
    },

    {
      id: 'company',
      key: 'company.financial.revenue',
      label: 'Company Revenue',
      size: 'medium',
      metadata: companyMetadata,
      excludeFromColumnModifier: true,
      render: (_, v) => (
        <CompanyAnnualRevenue
          value={v.company?.financial?.revenue?.annual?.amount}
        />
      ),
    },
  ],
  actions: [],
  filters: [],
  applyStateToQueryParams: true,
  browseApi: useBrowsePeopleSearchForTable,
  browseIdsApi: useBrowseSearchIds,
  apiResponsePerformanceMark: PEOPLE_SEARCH_API_RESPONSE_PERF_MARK,
  renderTablePrepend: (slot, { table, data }) => (
    <SearchTableHeader table={table} data={data}>
      <DataViewSwitcher />
    </SearchTableHeader>
  ),
  sx: { gap: 0.5 },
  hideColumnsOnNoData: true,
  emptyProps: SearchDataNotFound,
  rowsPerPageOptions: [25],
  pageSelectorMax: 100,
  maxStickyRows: 5,
  // maxHeight: 'calc(100vh - 312px)',
  manualSelectionLimit: 100,
  includeStickyRowsInSelection: true,
  bordered: 'solid',
  stickyHeader: true,
  showJumpToPage: true,
  prependSortingFields: { company_employee_count: 'desc' },
  enableUpdatedSearchItems: true,
};
